import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DemoRequestDialog } from '@/components/landing/DemoRequestDialog';
import { StructuredData } from '@/components/seo/StructuredData';
import { SpotlightCard } from '@/components/home/primitives';
import { CtaPanel, DataTable, FaqSection, HeroActions, InShort, PageHero, PageShell, Reveal, Section, SectionHeading } from '@/components/page/PageKit';
import { ALTERNATIVES_REVIEWED_LABEL, BEATVIEW, HUB_CRITERIA, HUB_FAQS, HUB_PLATFORM_NAMES, HUB_QUICK_ANSWER, HUB_SEO, buildHubSchemas, competitors, getHubReview, getPlatform } from '@/data/alternatives';
import { useSEO } from '@/hooks/useSEO';
import { COMPETITOR_ICONS } from '@/components/page/competitorIcons';

export default function Alternatives() {
  const [demo, setDemo] = useState(false);
  useSEO({ title: HUB_SEO.title, description: HUB_SEO.description, canonicalUrl: 'https://beatview.ai/alternatives' });
  const platforms = [BEATVIEW, ...competitors.map(c => getPlatform(c.id))];
  const review = getHubReview();

  return (
    <PageShell>
      <StructuredData data={buildHubSchemas()} />
      <PageHero breadcrumbs={[{ name: 'Alternatives' }]} eyebrow={`Hiring platform comparisons · Updated ${review.label}`} title={<>Compare AI interview and <span className="bv-hero-highlight">hiring platform alternatives</span></>} description={`Sourced, side-by-side comparisons of ${HUB_PLATFORM_NAMES}, covering interview formats, AI capabilities, integrations and published pricing.`}>
        <HeroActions onDemo={() => setDemo(true)} note="Every competitor detail links to the vendor’s own website. Beatview publishes these pages, and each one says where another tool is the better fit." />
      </PageHero>

      <Section tone="white" narrow>
        <InShort label="How the market splits">{HUB_QUICK_ANSWER}</InShort>
      </Section>

      <Section tone="wash" labelledBy="platforms-title">
        <SectionHeading id="platforms-title" eyebrow="Comparisons" title="Choose a platform to compare" description="Each guide covers what the platform does, its published pricing, the best alternatives for different teams, and a head-to-head with Beatview." />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {competitors.map((c, i) => {
            const Icon = COMPETITOR_ICONS[c.id];
            const p = getPlatform(c.id);
            return (
              <Reveal key={c.id} delay={(i % 3) * 0.06}>
                <Link to={`/alternatives/${c.id}`} className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
                  <SpotlightCard className="flex h-full flex-col p-6 sm:p-7">
                    <div className="flex items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-bv-blue-wash text-bv-blue transition-colors group-hover:bg-bv-ink group-hover:text-white"><Icon className="h-5 w-5" /></span>
                      <div className="min-w-0"><h3 className="font-display text-xl font-semibold text-bv-ink">{c.name} alternatives</h3><p className="mt-1 text-sm text-bv-mute">{p.category}</p></div>
                    </div>
                    <p className="mt-5 flex-1 text-[15px] leading-6 text-bv-ink2">Best for: {p.bestFor}.</p>
                    <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">Compare {c.alternativeIds.length} alternatives <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                  </SpotlightCard>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section tone="white" labelledBy="market-table-title">
        <SectionHeading id="market-table-title" eyebrow="Market overview" title="Hiring platforms side by side" description={`Interview formats, AI capabilities and entry pricing as published by each vendor, read between ${ALTERNATIVES_REVIEWED_LABEL} and ${review.label}.`} />
        <Reveal delay={0.1} className="mt-10">
          <DataTable
            caption="AI interview and hiring platforms compared by category, interview format, AI capabilities and starting price"
            columns={['Platform', 'Category', 'Interview format', 'AI capabilities', 'Starting price']}
            rows={platforms.map(p => [
              p.isBeatview ? <span key="n" className="font-semibold text-bv-ink">{p.name}<span className="block text-xs font-medium text-bv-mute">Our product</span></span> : <Link key="n" to={`/alternatives/${p.id}`} className="font-semibold text-bv-blue hover:underline">{p.name}</Link>,
              p.category,
              p.interviewFormats,
              p.aiSummary,
              p.startingPrice,
            ])}
            highlight={undefined}
            minWidth={980}
          />
        </Reveal>
      </Section>

      <Section tone="base" labelledBy="criteria-title">
        <SectionHeading id="criteria-title" eyebrow="Evaluation criteria" title="How to choose a hiring platform" description="Six checks that separate a good fit from a feature checklist." />
        <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {HUB_CRITERIA.map((reason, i) => (
            <Reveal key={reason.title} delay={(i % 3) * 0.06}>
              <li className="h-full rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                <span className="bv-hero-highlight font-display text-3xl font-semibold">0{i + 1}</span>
                <h3 className="mt-4 font-display text-lg font-semibold text-bv-ink">{reason.title}</h3>
                <p className="mt-2 text-[15px] leading-6 text-bv-ink2">{reason.description}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </Section>

      <FaqSection title="Hiring platform comparisons: frequently asked questions" items={HUB_FAQS} idPrefix="alternatives-faq" tone="white" />

      <CtaPanel eyebrow="Built for recruitment agencies" title={<>Resume screening, AI interviews and client sharing — <span className="bv-hero-highlight">in one workflow.</span></>} description="Start with one active job on the Free plan, then add AI video interviews on Pro when you are ready." onDemo={() => setDemo(true)} />
      <DemoRequestDialog open={demo} onOpenChange={setDemo} />
    </PageShell>
  );
}
